import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Currency, Organization, User } from 'models';
import {
  Checkbox,
  FormControlLabel,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Typography,
} from '@material-ui/core';
import { keyBy } from 'lodash';
import CurrencyTextField from 'components/shared/CurrencyTextField';
import Dialog from 'components/shared/Dialog';
import NumberFormat from 'react-number-format';
import { ReconVelocityTaskDefinition } from 'models/plugins/reconVelocityPlugin';
import {
  useUpdateTaskDefinition,
  useCreateTaskDefinition,
} from 'api/organizations/plugins/reconvelocity/tasks';
import { useOrganizationUsers } from 'api';

import './EditTaskTemplateDialog.scss';

type LaborType = 'FIXED' | 'HOURLY';

export interface EditTaskTemplateDialogProps {
  orgId: Organization['id'];
  taskDefinition: ReconVelocityTaskDefinition;
  onClose?: () => void;
}

const EditTaskTemplateDialog = ({
  orgId,
  taskDefinition,
  onClose,
}: EditTaskTemplateDialogProps) => {
  const { updateTaskDefinitionAsync } = useUpdateTaskDefinition(
    orgId,
    taskDefinition.id
  );
  const { createTaskDefinitionAsync } = useCreateTaskDefinition(orgId);
  const {
    data: organizationUsersData,
    isLoading: organizationUsersAreLoading,
  } = useOrganizationUsers(orgId);
  const organizationUsersDataPages = organizationUsersData?.pages;
  const organizationUsers = useMemo(
    () =>
      organizationUsersDataPages?.reduce(
        (acc: User[], pageUsers) => [...acc, ...pageUsers.data],
        []
      ) ?? [],
    [organizationUsersDataPages]
  ); // TODO use Memo or handle flat users differently

  const [
    [taskDefinitionInState, setTaskDefinitionInState],
    [laborType, setLaborType],
    [laborRate, setLaborRate],
    [laborHours, setLaborHours],
    [laborCost, setLaborCost],
    [partsCost, setPartsCost],
    [currency, setCurrency],
  ] = [
    useState(taskDefinition),
    useState<LaborType>(
      taskDefinition.defaultLaborRate.amount ? 'HOURLY' : 'FIXED'
    ),
    useState(taskDefinition.defaultLaborRate?.amount),
    useState(taskDefinition.defaultLaborHours),
    useState(taskDefinition.defaultLaborCost?.amount),
    useState(taskDefinition.defaultPartsCost?.amount),
    useState<Currency>(taskDefinition.defaultLaborCost.currency ?? 'USD'),
  ];

  const organizationUsersMap = useMemo(
    () => keyBy(organizationUsers, 'id'),
    [organizationUsers]
  );

  useEffect(() => {
    if (laborType === 'FIXED') return;
    setLaborCost((laborRate ?? 0) * laborHours);
  }, [laborHours, laborRate, laborType, setLaborCost]);

  const onPrimary = useCallback(async () => {
    const submittableTaskDefinition = {
      ...taskDefinitionInState,
      defaultLaborHours: laborHours,
      defaultLaborCost: {
        amount: laborCost,
        currency,
      },
      defaultPartsCost: {
        amount: partsCost,
        currency,
      },
    };

    if (laborType === 'HOURLY') {
      delete submittableTaskDefinition.defaultLaborCost.amount;
      submittableTaskDefinition.defaultLaborRate = {
        amount: laborRate,
        currency,
      };
      submittableTaskDefinition.defaultLaborCost = {
        amount: (laborRate ?? 0) * laborHours,
        currency,
      };
    } else {
      submittableTaskDefinition.defaultLaborRate.amount = 0;
    }

    if (taskDefinition.id) {
      await updateTaskDefinitionAsync(submittableTaskDefinition);
    } else {
      await createTaskDefinitionAsync(submittableTaskDefinition);
    }
  }, [
    currency,
    laborCost,
    laborHours,
    laborRate,
    laborType,
    partsCost,
    taskDefinition.id,
    taskDefinitionInState,
    updateTaskDefinitionAsync,
    createTaskDefinitionAsync,
  ]);

  // -- Form components
  const renderTaskTitleTextField = () => (
    <TextField
      id="task-type-textfield"
      required
      fullWidth
      variant="outlined"
      label="Type"
      margin="dense"
      value={taskDefinitionInState.title}
      helperText="The title of the task template"
      onChange={({ target: { value: title } }) =>
        setTaskDefinitionInState((current) => ({ ...current, title }))
      }
      autoFocus
    />
  );

  const renderDefaultAssigneeSelect = () => (
    <FormControl variant="outlined" fullWidth>
      <InputLabel shrink variant="outlined">
        Default Assignee
      </InputLabel>
      <Select
        id="default-assignee-textfield"
        variant="outlined"
        label="Default Assignee"
        value={taskDefinitionInState.defaultAssigneeId ?? ''}
        margin="dense"
        displayEmpty
        renderValue={(value) => {
          const userId = value as string;
          if (!userId) return 'None';

          const user = organizationUsersMap[userId];
          return `${user.firstName} ${user.lastName}`;
        }}
        onChange={({ target: { value } }) =>
          setTaskDefinitionInState((current) => ({
            ...current,
            defaultAssigneeId: value ? (value as string) : null,
          }))
        }
      >
        <MenuItem value="">None</MenuItem>
        {organizationUsers?.map((user) => (
          <MenuItem
            key={user?.id}
            value={user?.id}
            id="default-assignee-option"
          >
            {user.firstName} {user.lastName}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  const renderDescriptionTextField = () => (
    <TextField
      variant="outlined"
      label="Description"
      multiline
      rows={4}
      fullWidth
      value={taskDefinitionInState.description ?? ''}
      onChange={({ target: { value: description } }) =>
        setTaskDefinitionInState((current) => ({
          ...current,
          description,
        }))
      }
      id="description-textfield"
    />
  );

  const renderAddByDefaultForNewReconVehiclesCheckbox = () => (
    <FormControlLabel
      control={
        <Checkbox
          checked={taskDefinitionInState.addByDefaultForNewRecon}
          onChange={({ target: { checked: addByDefaultForNewRecon } }) =>
            setTaskDefinitionInState((current) => ({
              ...current,
              addByDefaultForNewRecon,
            }))
          }
        />
      }
      label="Add by Default to New Vehicles"
    />
  );

  const renderRequiresApprovalCheckbox = () => (
    <FormControlLabel
      control={
        <Checkbox
          checked={taskDefinitionInState.requiresApproval}
          onChange={({ target: { checked: requiresApproval } }) =>
            setTaskDefinitionInState((current) => ({
              ...current,
              requiresApproval,
            }))
          }
        />
      }
      label="Requires Approval"
    />
  );

  const renderLaborTypeSelect = () => (
    <FormControl variant="outlined" fullWidth>
      <InputLabel>Labor Type</InputLabel>
      <Select
        id="labor-type-textfield"
        variant="outlined"
        label="Labor Type"
        margin="dense"
        value={laborType}
        required
        onChange={({ target: { value } }) => setLaborType(value as LaborType)}
      >
        <MenuItem value="HOURLY">Hourly</MenuItem>
        <MenuItem value="FIXED">Fixed Cost</MenuItem>
      </Select>
    </FormControl>
  );

  const DefaultLaborHoursTextField = (
    <NumberFormat
      required
      fullWidth
      variant="outlined"
      label="Hours"
      margin="dense"
      customInput={TextField}
      value={laborHours}
      onValueChange={({ value }) => {
        if (value === '') return;
        setLaborHours(parseFloat(value));
      }}
    />
  );

  const DefaultLaborRateTextField = (
    <CurrencyTextField
      required
      fullWidth
      variant="outlined"
      label="Hourly Rate"
      name="defaultLaborRate"
      margin="dense"
      value={laborRate ?? 0}
      onValueChange={({ value }) => {
        if (value === '') return;
        setLaborRate(parseFloat(value));
      }}
    />
  );

  const DefaultLaborCostDisplay = (
    <FormControl
      style={{
        flexDirection: 'row',
        justifyContent: 'space-between',
      }}
      fullWidth
    >
      <Typography variant="subtitle1">Labor Cost</Typography>
      <NumberFormat
        value={laborCost ?? 0}
        displayType="text"
        renderText={(formattedValue) => (
          <Typography variant="subtitle1">{formattedValue}</Typography>
        )}
        prefix="$"
        thousandSeparator
        decimalScale={2}
        fixedDecimalScale
      />
    </FormControl>
  );

  const DefaultLaborCostTextField = (
    <CurrencyTextField
      id="labor-cost-textfield"
      required
      fullWidth
      variant="outlined"
      label="Labor Cost"
      margin="dense"
      value={laborCost ?? 0}
      onValueChange={({ value }) => {
        if (value === '') return;
        setLaborCost(parseFloat(value));
      }}
    />
  );

  const DefaultPartsCostTextField = (
    <CurrencyTextField
      id="parts-cost-textfield"
      required
      fullWidth
      variant="outlined"
      label="Parts Cost"
      margin="dense"
      value={partsCost ?? 0}
      onValueChange={({ value }) => setPartsCost(parseFloat(value))}
    />
  );

  const renderTotal = () => (
    <FormControl
      style={{ flexDirection: 'row', justifyContent: 'space-between' }}
      fullWidth
    >
      <Typography variant="subtitle1">Total</Typography>
      <NumberFormat
        value={(laborCost ?? 0) + (partsCost ?? 0)}
        displayType="text"
        renderText={(formattedValue) => (
          <Typography variant="subtitle1">{formattedValue}</Typography>
        )}
        prefix="$"
        thousandSeparator
        decimalScale={2}
        fixedDecimalScale
      />
    </FormControl>
  );

  const renderCurrencySelect = () => (
    <FormControl variant="outlined" fullWidth>
      <InputLabel>Currency</InputLabel>
      <Select
        id="current-select-textfield"
        variant="outlined"
        margin="dense"
        label="Currency"
        value={currency}
        onChange={({ target: { value } }) => setCurrency(value as Currency)}
      >
        <MenuItem value="USD">USD</MenuItem>
        <MenuItem value="CAD">CAD</MenuItem>
      </Select>
    </FormControl>
  );

  return (
    <>
      <Dialog
        open
        title={`${taskDefinition.id ? 'Edit' : 'Add'} Task Template`}
        maxWidth="md"
        primaryButtonLabel="Save"
        onPrimary={onPrimary}
        onClose={onClose}
        block={organizationUsersAreLoading}
        className="PluginDialog EditTaskTemplateDialog"
      >
        <div className="flex-columns">
          <div style={{ flex: 2 }}>
            {renderTaskTitleTextField()}

            <div className="spacer" />

            {renderDefaultAssigneeSelect()}

            <div className="spacer" />

            {renderDescriptionTextField()}

            <div className="spacer" />

            {renderAddByDefaultForNewReconVehiclesCheckbox()}

            {renderRequiresApprovalCheckbox()}
          </div>

          <div className="spacer" />

          <div style={{ flex: 1 }}>
            {renderLaborTypeSelect()}

            <div className="spacer" />

            {laborType === 'HOURLY' ? (
              <>
                {DefaultLaborHoursTextField}

                {DefaultLaborRateTextField}

                <hr />

                {DefaultLaborCostDisplay}
              </>
            ) : (
              DefaultLaborCostTextField
            )}

            <div className="spacer" />

            {DefaultPartsCostTextField}

            <hr />

            {renderTotal()}

            <div className="spacer" />

            {renderCurrencySelect()}
          </div>
        </div>
        <div className="spacer" />
      </Dialog>
    </>
  );
};

export default EditTaskTemplateDialog;
